import { useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  berlinDate,
  DELIVERY_METHOD_LABELS,
  DELIVERY_NEEDS_SCAN,
  DOCUMENT_MAX_BYTES,
  TERMINATION_FORM_LABELS,
  TERMINATION_KIND_LABELS,
  TERMINATION_STATUS_LABELS,
  type Contract,
  type ContractTermination,
  type DeliveryMethod,
} from '@kontor/shared';
import { api, apiUpload, ApiError, queryString } from '../../api';
import { useSession } from '../../session';
import { deDate } from './TerminationFormPage';
import './contracts.css';

import { EntityHistory } from '../../components/EntityHistory';
const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });

const methodsFor = (t: ContractTermination): DeliveryMethod[] => (t.initiatedBy === 'party' ? ['received'] : t.form === 'written' ? ['hand_witness', 'registered_mail', 'courier', 'bailiff'] : ['email_viewed']);

/** Eine Kündigung: Angaben, Schreiben bearbeiten, freigeben, Zugang erfassen, zurückziehen und die PDFs. Der Link der elektronischen Kündigung ist nur direkt nach der Freigabe zu sehen. */
export function TerminationDetailPage() {
  const { id = '', tid = '' } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const { can } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [issuing, setIssuing] = useState(false);
  const [password, setPassword] = useState('');
  const [link, setLink] = useState<{ url: string; mailSent: boolean } | null>(null);
  const [recording, setRecording] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState('');
  const [text, setText] = useState<string | null>(null);
  const [deliveryDate, setDeliveryDate] = useState(berlinDate());
  const [method, setMethod] = useState<DeliveryMethod | ''>('');
  const [witness, setWitness] = useState('');
  const [note, setNote] = useState('');
  const [stated, setStated] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  const contract = useQuery({ queryKey: ['contract', id], queryFn: async () => (await api<{ data: Contract }>('GET', `/contracts/${id}`)).data });
  const t = contract.data?.terminations.find((x) => x.id === tid);

  const refresh = () => {
    client.invalidateQueries({ queryKey: ['contract', id] });
    client.invalidateQueries({ queryKey: ['contracts'] });
    client.invalidateQueries({ queryKey: ['documents'] });
  };
  const fail = (e: unknown) => {
    if (e instanceof ApiError && e.status === 422) {
      setFieldErrors(e.fields);
      setError(Object.keys(e.fields).length === 0 ? e.message : null);
    } else setError(e instanceof ApiError ? e.message : 'Das hat nicht geklappt.');
  };
  const clear = () => {
    setError(null);
    setFieldErrors({});
  };

  const saveText = useMutation({
    mutationFn: (value: string | null) => api<{ data: ContractTermination }>('PATCH', `/contracts/${id}/terminations/${tid}`, { noticeText: value }),
    onSuccess: () => {
      setText(null);
      refresh();
    },
    onError: fail,
  });
  const issue = useMutation({
    mutationFn: () => api<{ data: ContractTermination; link: string | null; mailSent: boolean }>('POST', `/contracts/${id}/terminations/${tid}/issue`, { password }),
    onSuccess: (r) => {
      refresh();
      setIssuing(false);
      setPassword('');
      if (r.link) setLink({ url: r.link, mailSent: r.mailSent });
    },
    onError: fail,
  });
  const record = useMutation({
    mutationFn: async () => {
      const file = fileInput.current?.files?.[0];
      if (file && file.size > DOCUMENT_MAX_BYTES) throw new ApiError(422, 'validation_failed', 'Die Datei ist zu groß.', { file: 'Die Datei ist zu groß, erlaubt sind 20 MB.' });
      const path = `/contracts/${id}/terminations/${tid}/delivery${queryString({ deliveryDate, method, witness: witness.trim(), note: note.trim(), effectiveDate: stated })}`;
      return file ? apiUpload<{ data: ContractTermination }>(path, new File([file], file.name, { type: 'application/octet-stream' })) : api<{ data: ContractTermination }>('POST', path);
    },
    onSuccess: () => {
      refresh();
      setRecording(false);
    },
    onError: fail,
  });
  const withdraw = useMutation({
    mutationFn: () => api<{ data: ContractTermination }>('POST', `/contracts/${id}/terminations/${tid}/withdraw`, { reason: reason.trim() || undefined }),
    onSuccess: () => {
      refresh();
      setWithdrawing(false);
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/contracts/${id}/terminations/${tid}`),
    onSuccess: () => {
      refresh();
      navigate(`/vertraege/${id}`, { replace: true });
    },
    onError: fail,
  });

  if (contract.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!contract.data || !t) return <div className="card narrow"><h1>Kündigung nicht gefunden</h1><Link className="btn btn--secondary" to={`/vertraege/${id}`}>Zurück zum Vertrag</Link></div>;
  const c = contract.data;
  const busy = issue.isPending || record.isPending || withdraw.isPending || remove.isPending || saveText.isPending;
  const methods = methodsFor(t);
  const chosenMethod = method || methods[0]!;
  const needsScan = DELIVERY_NEEDS_SCAN.includes(chosenMethod);
  const ended = t.status === 'delivered' && t.effectiveDate && t.effectiveDate <= berlinDate();

  const submitIssue = (e: FormEvent) => {
    e.preventDefault();
    clear();
    issue.mutate();
  };
  const submitRecord = (e: FormEvent) => {
    e.preventDefault();
    clear();
    if (needsScan && !fileInput.current?.files?.[0]) return setFieldErrors({ file: 'Bitte laden Sie den Scan hoch.' });
    record.mutate();
  };

  return (
    <>
      <p><Link to={`/vertraege/${id}`} className="back-link">← Zurück zum Vertrag</Link></p>
      <div className="page-head">
        <h1>Kündigung {t.number}</h1>
        <span className={`badge badge--termination-${t.status}`}>{TERMINATION_STATUS_LABELS[t.status]}</span>
      </div>
      <p className="muted">{c.title} · {c.number} · {c.partyName}</p>

      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {t.status === 'delivered' && (
        <div className="alert alert--ok" role="status">
          Zugegangen am {t.deliveryDate ? deDate(t.deliveryDate) : '–'}. Der Vertrag endet {t.effectiveDate ? (ended ? `seit dem ${deDate(t.effectiveDate)}` : `am ${deDate(t.effectiveDate)}`) : 'zu einem noch offenen Termin'}.
          {t.hasProof ? ' Nachweisblatt und Scan liegen in Dokumente, beim Vertragspartner und im allgemeinen Bestand.' : ' Das Nachweisblatt konnte nicht erzeugt werden, bitte versuchen Sie es später erneut.'}
        </div>
      )}
      {t.status === 'withdrawn' && <div className="alert alert--warn" role="status">Die Kündigung ist zurückgezogen.{t.withdrawReason ? ` Grund: ${t.withdrawReason}` : ''}</div>}
      {t.status === 'issued' && t.form === 'written' && (
        <div className="alert alert--warn" role="status">
          <strong>Nächste Schritte:</strong> Schreiben als PDF laden und ausdrucken, von einer vertretungsberechtigten Person eigenhändig unterschreiben lassen, so übergeben, dass sich der Zugang beweisen lässt (Zeuge, Einschreiben,
          Bote), und danach hier den Zugang mit dem Scan des unterschriebenen Schreibens erfassen.
        </div>
      )}
      {t.status === 'issued' && t.form === 'electronic' && <div className="alert alert--warn" role="status">Wir warten auf die Bestätigung des Empfangs durch {c.partyName}. Hat die Person den Link nur geöffnet, lässt sich der Zugang darüber festhalten.</div>}

      {link && (
        <div className="card">
          <h2>{link.mailSent ? 'Kündigung gesendet' : 'Freigegeben, aber die E-Mail ging nicht hinaus'}</h2>
          <p className="muted small">{link.mailSent ? `Eine E-Mail mit dem Schreiben und dem Link ging an ${c.partyEmail}.` : 'Es ist kein E-Mail-Versand eingerichtet. Geben Sie den Link selbst weiter.'} Der Link ist nur jetzt sichtbar.</p>
          <div className="contract-link">
            <input className="input" readOnly value={link.url} onFocus={(e) => e.currentTarget.select()} aria-label="Link zur Bestätigung" />
            <button className="btn btn--secondary" type="button" onClick={() => void navigator.clipboard?.writeText(link.url)}>Link kopieren</button>
          </div>
        </div>
      )}

      <div className="form-actions">
        {t.status === 'draft' && can('contracts.write') && <Link className="btn btn--secondary" to={`/vertraege/${id}/kuendigung/${t.id}/bearbeiten`}>Angaben ändern</Link>}
        {t.status === 'draft' && t.initiatedBy === 'company' && can('contracts.send') && <button className="btn btn--primary" type="button" disabled={busy} onClick={() => { clear(); setIssuing(true); }}>Freigeben</button>}
        {((t.status === 'draft' && t.initiatedBy === 'party') || t.status === 'issued') && can('contracts.send') && <button className="btn btn--primary" type="button" disabled={busy} onClick={() => { clear(); setRecording(true); }}>{t.initiatedBy === 'party' ? 'Eingang erfassen' : 'Zugang erfassen'}</button>}
        {t.status === 'issued' && can('contracts.send') && <button className="btn btn--ghost" type="button" disabled={busy} onClick={() => setWithdrawing(true)}>Zurückziehen</button>}
        {t.initiatedBy === 'company' && <a className="btn btn--secondary" href={`/api/v1/contracts/${id}/terminations/${t.id}/pdf`} target="_blank" rel="noreferrer">{t.status === 'draft' ? 'Schreiben ansehen' : 'Schreiben als PDF'}</a>}
        {t.hasProof && <a className="btn btn--secondary" href={`/api/v1/contracts/${id}/terminations/${t.id}/pdf?kind=proof`} target="_blank" rel="noreferrer">Nachweisblatt</a>}
        {t.status === 'draft' && can('contracts.delete') && <button className="btn btn--danger btn--sm" type="button" disabled={busy} onClick={() => { if (window.confirm('Entwurf endgültig löschen?')) remove.mutate(); }}>Entwurf löschen</button>}
      </div>

      {issuing && (
        <form className="card narrow" onSubmit={submitIssue} noValidate>
          <h2>Kündigung freigeben</h2>
          <p className="muted small">
            Mit Ihrem Passwort geben Sie das Schreiben frei. Danach lässt es sich nicht mehr ändern.{' '}
            {t.form === 'electronic' ? `${c.partyName} bekommt es sofort per E-Mail mit einem Link zur Bestätigung des Empfangs.` : 'Sie drucken es danach aus, unterschreiben es von Hand und übergeben es. Den Zugang erfassen Sie anschließend hier.'}
          </p>
          <label className="field">
            <span>Ihr Passwort</span>
            <input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} aria-invalid={!!fieldErrors.password} />
            {fieldErrors.password && <span className="field__error" role="alert">{fieldErrors.password}</span>}
            {fieldErrors.noticeText && <span className="field__error" role="alert">{fieldErrors.noticeText}</span>}
          </label>
          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={issue.isPending || password.length === 0}>{issue.isPending ? 'Wird freigegeben …' : 'Freigeben'}</button>
            <button className="btn btn--secondary" type="button" disabled={issue.isPending} onClick={() => setIssuing(false)}>Abbrechen</button>
          </div>
        </form>
      )}

      {recording && (
        <form className="card narrow" onSubmit={submitRecord} noValidate>
          <h2>{t.initiatedBy === 'party' ? 'Eingang der Kündigung erfassen' : 'Zugang der Kündigung erfassen'}</h2>
          <p className="muted small">Der Zugang bestimmt den Beginn der Frist. Halten Sie fest, was Sie später belegen können.</p>
          <label className="field">
            <span>{t.initiatedBy === 'party' ? 'Eingegangen am' : 'Zugegangen am'}</span>
            <input className="input" type="date" max={berlinDate()} value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} style={{ maxWidth: '12rem' }} aria-invalid={!!fieldErrors.deliveryDate} />
            {fieldErrors.deliveryDate && <span className="field__error" role="alert">{fieldErrors.deliveryDate}</span>}
          </label>
          <label className="field">
            <span>Art des Zugangs</span>
            <select className="input" value={chosenMethod} onChange={(e) => setMethod(e.target.value as DeliveryMethod)}>
              {methods.map((m) => <option key={m} value={m}>{DELIVERY_METHOD_LABELS[m]}</option>)}
            </select>
            {fieldErrors.method && <span className="field__error" role="alert">{fieldErrors.method}</span>}
          </label>
          {chosenMethod === 'hand_witness' && (
            <label className="field">
              <span>Name des Zeugen</span>
              <input className="input" value={witness} maxLength={200} onChange={(e) => setWitness(e.target.value)} />
              <span className="muted small">Der Zeuge sollte den Inhalt des Schreibens kennen und die Übergabe bezeugen können.</span>
            </label>
          )}
          {t.initiatedBy === 'party' && (
            <label className="field">
              <span>Beendigungstermin laut Schreiben (falls genannt)</span>
              <input className="input" type="date" value={stated} onChange={(e) => setStated(e.target.value)} style={{ maxWidth: '12rem' }} />
              <span className="muted small">Liegt er vor dem Termin, den die Frist ergibt, gilt der spätere.</span>
            </label>
          )}
          {needsScan && (
            <label className="field">
              <span>{t.initiatedBy === 'party' ? 'Scan des Schreibens' : 'Scan des unterschriebenen Schreibens oder des Belegs (Einlieferungsbeleg, Zustellprotokoll)'}</span>
              <input ref={fileInput} className="input" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" />
              {fieldErrors.file && <span className="field__error" role="alert">{fieldErrors.file}</span>}
              <span className="muted small">Die Datei landet in Dokumente, bei der Karte des Vertragspartners und im allgemeinen Bestand. Ein Scan ersetzt nicht das Original, bewahren Sie es auf.</span>
            </label>
          )}
          <label className="field">
            <span>Anmerkung (freiwillig)</span>
            <input className="input" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
          </label>
          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={record.isPending}>{record.isPending ? 'Wird gespeichert …' : 'Zugang festhalten'}</button>
            <button className="btn btn--secondary" type="button" disabled={record.isPending} onClick={() => setRecording(false)}>Abbrechen</button>
          </div>
        </form>
      )}

      {withdrawing && (
        <div className="card narrow">
          <h2>Kündigung zurückziehen</h2>
          <p className="muted small">Nur möglich, solange sie dem Empfänger nicht zugegangen ist. Danach lässt sie sich nur noch im Einvernehmen aufheben.</p>
          <label className="field">
            <span>Grund (freiwillig)</span>
            <input className="input" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
          </label>
          <div className="form-actions">
            <button className="btn btn--danger" type="button" disabled={withdraw.isPending} onClick={() => withdraw.mutate()}>Zurückziehen</button>
            <button className="btn btn--secondary" type="button" onClick={() => setWithdrawing(false)}>Abbrechen</button>
          </div>
        </div>
      )}

      <div className="contract-grid contract-grid--2">
        <section className="card">
          <h2>{t.initiatedBy === 'company' ? 'Kündigungsschreiben' : 'Kündigung des Vertragspartners'}</h2>
          {t.initiatedBy === 'party' ? (
            <p className="muted">Das Schreiben des Vertragspartners wird als Scan erfasst und liegt danach in Dokumente.</p>
          ) : text !== null && t.status === 'draft' ? (
            <>
              <textarea className="input" style={{ minHeight: '24rem' }} value={text} onChange={(e) => setText(e.target.value)} />
              {fieldErrors.noticeText && <span className="field__error" role="alert">{fieldErrors.noticeText}</span>}
              <div className="form-actions">
                <button className="btn btn--primary" type="button" disabled={busy} onClick={() => saveText.mutate(text)}>Text speichern</button>
                <button className="btn btn--secondary" type="button" disabled={busy} onClick={() => setText(null)}>Abbrechen</button>
              </div>
            </>
          ) : (
            <>
              <pre className="contract-body contract-body--box">{t.noticeText}</pre>
              {t.status === 'draft' && can('contracts.write') && (
                <div className="form-actions">
                  <button className="btn btn--secondary btn--sm" type="button" onClick={() => setText(t.noticeText)}>Text bearbeiten</button>
                  <button className="btn btn--ghost btn--sm" type="button" disabled={busy} onClick={() => { if (window.confirm('Den Text aus der Vorlage neu erzeugen? Ihre Änderungen am Text gehen verloren.')) saveText.mutate(null); }}>Aus Vorlage neu erzeugen</button>
                </div>
              )}
            </>
          )}
        </section>
        <section className="card">
          <h2>Angaben</h2>
          <dl className="contract-facts">
            <dt>Gekündigt von</dt><dd>{t.initiatedBy === 'company' ? 'Unternehmen' : c.partyName}</dd>
            <dt>Art</dt><dd>{TERMINATION_KIND_LABELS[t.kind]}</dd>
            <dt>Form</dt><dd>{TERMINATION_FORM_LABELS[t.form]}</dd>
            {t.reason && (<><dt>Grund</dt><dd>{t.reason}</dd></>)}
            <dt>Zugang geplant</dt><dd>{deDate(t.plannedDeliveryDate)}</dd>
            <dt>Termin im Schreiben</dt><dd>{deDate(t.letterEffectiveDate)}</dd>
            <dt>Frist</dt><dd>{t.noticeLabel || '–'}</dd>
            {t.deliveryDate && (<><dt>Zugegangen</dt><dd>{deDate(t.deliveryDate)}{t.deliveryMethod ? `, ${DELIVERY_METHOD_LABELS[t.deliveryMethod]}` : ''}</dd></>)}
            {t.deliveryWitness && (<><dt>Zeuge</dt><dd>{t.deliveryWitness}</dd></>)}
            {t.deliveryNote && (<><dt>Anmerkung</dt><dd>{t.deliveryNote}</dd></>)}
            <dt>Vertrag endet</dt><dd>{t.effectiveDate ? deDate(t.effectiveDate) : 'steht mit dem Zugang fest'}</dd>
            {t.issuedAt && (<><dt>Freigegeben</dt><dd>{stamp.format(new Date(t.issuedAt))}</dd></>)}
          </dl>
          {t.documentHash && (<><h3>Prüfsumme des Schreibens</h3><p className="contract-hash">{t.documentHash}</p></>)}
          {t.scanHash && (<><h3>Prüfsumme des Scans</h3><p className="contract-hash">{t.scanHash}</p></>)}
          {t.proofHash && (<><h3>Prüfsumme des Nachweisblatts</h3><p className="contract-hash">{t.proofHash}</p></>)}
        </section>
      </div>
      <EntityHistory entity="contract_termination" id={tid} />
    </>
  );
}
